import {ActionTree, GetterTree, Module, MutationTree} from "vuex";
import senkronPlanService, {
    SenkronPlan,
    SenkronPlanGovde,
    SenkronPlanYazmaCevabi,
    SenkronRaporCevap
} from "@/services/senkronPlanService";

/**
 * Senkron planları ve sabah raporu durumu (Faz 3, DEV-2966 / DEV-2968).
 *
 * Rapor iki yerde gösteriliyor (Ö-P2 kararı): İşlemler sayfasındaki şerit ve
 * plan ekranındaki koşum geçmişi. Aynı günü iki kez çekmesinler diye rapor gün
 * anahtarlı bir önbellekte tutulur — öneri kutusu/rozet ikilisiyle aynı gerekçe.
 */
export interface SenkronPlanState {
    planlar: Array<SenkronPlan>;
    yukleniyor: boolean;
    /** Son yükleme hatası (kullanıcıya gösterilen sabit Türkçe metin). */
    hata: string | null;
    /** En az bir kez yükleme denendi mi (başarılı ya da başarısız). */
    denendiMi: boolean;
    /** gun ("YYYY-AA-GG") -> rapor cevabı. */
    raporlar: Record<string, SenkronRaporCevap>;
    raporYukleniyor: boolean;
    raporHatasi: string | null;
}

/** `gun` verilmeden istenen rapor için in-flight anahtarı. */
const BUGUN_ANAHTARI = "__bugun__";

/**
 * Süren rapor istekleri (gün -> promise). Vuex STATE'İNDE tutulmaz: state
 * serileştirilebilir kalmalı ve promise reaktif olmamalı. Her istek `finally`da
 * kendini siler, dolayısıyla testler arasında kalıntı bırakmaz.
 */
const raporIstekleri: Record<string, Promise<SenkronRaporCevap | null>> = {};

/**
 * Süren `planlariYukle` isteği — aynı gerekçeyle (yukarıdaki `raporIstekleri`
 * gibi) Vuex STATE'İNDE tutulmaz. `finally`da temizlenir.
 */
let planIstegi: Promise<Array<SenkronPlan>> | null = null;

/**
 * Fabrika olarak yazıldı: modül birden çok store'a kaydedilirse (birim
 * testlerinde her `it` için taze store kurulur) durumun paylaşılmaması gerekiyor.
 */
const state = (): SenkronPlanState => ({
    planlar: [],
    yukleniyor: false,
    hata: null,
    denendiMi: false,
    raporlar: {},
    raporYukleniyor: false,
    raporHatasi: null
});

const mutations: MutationTree<SenkronPlanState> = {
    SET_PLANLAR(state, planlar: Array<SenkronPlan>) {
        state.planlar = planlar;
    },
    SET_YUKLENIYOR(state, yukleniyor: boolean) {
        state.yukleniyor = yukleniyor;
    },
    SET_HATA(state, hata: string | null) {
        state.hata = hata;
    },
    SET_DENENDI(state, denendiMi: boolean) {
        state.denendiMi = denendiMi;
    },
    /** Tek planı yerinde günceller; yoksa listeye ekler (POST sonrası). */
    PLAN_YAZ(state, plan: SenkronPlan) {
        const sira = state.planlar.findIndex((p) => p.id === plan.id);
        if (sira === -1) {
            state.planlar = state.planlar.concat([plan]);
        } else {
            // Vue 2 dizide indeks ataması reaktif değil; yeni dizi kurulur.
            state.planlar = state.planlar.map((p) => (p.id === plan.id ? plan : p));
        }
    },
    PLAN_CIKAR(state, id: number) {
        state.planlar = state.planlar.filter((plan) => plan.id !== id);
    },
    SET_RAPOR(state, payload: { gun: string; rapor: SenkronRaporCevap }) {
        // Vue 2: yeni nesne kurulur, aksi halde eklenen anahtar reaktif olmaz.
        state.raporlar = {...state.raporlar, [payload.gun]: payload.rapor};
    },
    SET_RAPOR_YUKLENIYOR(state, yukleniyor: boolean) {
        state.raporYukleniyor = yukleniyor;
    },
    SET_RAPOR_HATASI(state, hata: string | null) {
        state.raporHatasi = hata;
    }
};

const getters: GetterTree<SenkronPlanState, any> = {
    planlar: (state) => state.planlar,
    aktifPlanSayisi: (state) => state.planlar.filter((plan) => plan.aktif).length,
    planYukleniyor: (state) => state.yukleniyor,
    planDenendiMi: (state) => state.denendiMi,
    planHatasi: (state) => state.hata,
    raporByGun: (state) => (gun: string) => state.raporlar[gun] ?? null,
    raporYukleniyor: (state) => state.raporYukleniyor,
    raporHatasi: (state) => state.raporHatasi
};

const actions: ActionTree<SenkronPlanState, any> = {
    /**
     * Planları çeker. Tek koruma eşzamanlılıktır (plan ekranı ile ön-kontrol aynı
     * anda isteyebilir); hata KALICI OLARAK yeniden denemeyi engellemez.
     *
     * kod inceleme: SÜREN İSTEĞE KATILINIR, bayat `state.planlar` DÖNÜLMEZ —
     * `raporYukle`deki aynı desen. Eskiden "yükleniyorsa çık" koruması ikinci
     * çağırana isteğin BAŞINDAKİ (olası boş/eski) listeyi döndürüyordu.
     */
    async planlariYukle({commit, state}): Promise<Array<SenkronPlan>> {
        if (planIstegi) return planIstegi;

        const istek = (async (): Promise<Array<SenkronPlan>> => {
            commit("SET_YUKLENIYOR", true);
            try {
                const planlar = await senkronPlanService.listele();
                commit("SET_PLANLAR", planlar);
                commit("SET_HATA", null);
                return planlar;
            } catch (e: any) {
                // Ham axios mesajı hem İngilizce hem de global uyarıyla çakışıyor.
                console.error("Senkron planları alınamadı:", e);
                commit("SET_HATA", "Senkron planları alınamadı.");
                return state.planlar;
            } finally {
                commit("SET_DENENDI", true);
                commit("SET_YUKLENIYOR", false);
                planIstegi = null;
            }
        })();

        planIstegi = istek;
        return istek;
    },

    /**
     * Yeni plan. Cevap 201 olsa bile üretim hata almış olabilir (`hata` dolu);
     * çağıran taraf `mesaj`ı her hâlükârda gösterir.
     */
    async planOlustur({commit}, govde: SenkronPlanGovde): Promise<SenkronPlanYazmaCevabi> {
        const cevap = await senkronPlanService.olustur(govde);
        commit("PLAN_YAZ", cevap);
        return cevap;
    },

    async planGuncelle(
        {commit},
        payload: { id: number; govde: SenkronPlanGovde }
    ): Promise<SenkronPlanYazmaCevabi> {
        const cevap = await senkronPlanService.guncelle(payload.id, payload.govde);
        commit("PLAN_YAZ", cevap);
        return cevap;
    },

    /** Yalnız `aktif` gönderir — bkz. `senkronPlanService.aktifDegistir`. */
    async planAktifDegistir(
        {commit},
        payload: { id: number; aktif: boolean }
    ): Promise<SenkronPlanYazmaCevabi> {
        const cevap = await senkronPlanService.aktifDegistir(payload.id, payload.aktif);
        commit("PLAN_YAZ", cevap);
        return cevap;
    },

    async planSil({commit}, id: number) {
        const cevap = await senkronPlanService.sil(id);
        commit("PLAN_CIKAR", id);
        return cevap;
    },

    /**
     * Gün bazlı rapor. Önbellekte varsa istek atılmaz (`zorla` ile atlanır).
     * `gun` verilmezse uç bugünü kullanır; önbellek anahtarı cevabın `gun`u olur.
     *
     * SÜREN İSTEĞE KATILINIR, erken `null` DÖNÜLMEZ. Basit bir "yükleniyorsa çık"
     * koruması sabah bildirimini sessizce düşürüyordu: 08:00'de zamanlayıcı raporu
     * isterken kullanıcı plan ekranını açmışsa istek süren sayılıyor, çağrı `null`
     * alıyor ve o gün hiç bildirim çıkmıyordu (zamanlayıcı günü zaten damgalamış
     * olduğu için ikinci bir şans da yoktu).
     */
    async raporYukle(
        {commit, state},
        payload: { gun?: string; zorla?: boolean } = {}
    ): Promise<SenkronRaporCevap | null> {
        const gun = payload.gun;
        const anahtar = gun ?? BUGUN_ANAHTARI;

        if (gun && !payload.zorla && state.raporlar[gun]) {
            return state.raporlar[gun];
        }
        if (!payload.zorla && raporIstekleri[anahtar]) {
            return raporIstekleri[anahtar];
        }

        const istek = (async (): Promise<SenkronRaporCevap | null> => {
            commit("SET_RAPOR_YUKLENIYOR", true);
            try {
                const rapor = await senkronPlanService.rapor(gun);
                if (rapor.gun) commit("SET_RAPOR", {gun: rapor.gun, rapor});
                commit("SET_RAPOR_HATASI", null);
                return rapor;
            } catch (e: any) {
                console.error("Senkron raporu alınamadı:", e);
                commit("SET_RAPOR_HATASI", "Senkron raporu alınamadı.");
                return null;
            } finally {
                commit("SET_RAPOR_YUKLENIYOR", false);
                delete raporIstekleri[anahtar];
            }
        })();

        raporIstekleri[anahtar] = istek;
        return istek;
    }
};

const senkronPlan: Module<SenkronPlanState, any> = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};

export default senkronPlan;
